import { useEffect, useState, useRef, useCallback } from "react";
import { API_URL } from "constants/index";
import { fetchWithAuth } from "shared/api/fetch-with-auth";
import * as Sentry from "@sentry/react";
import type {
  DropzoneData,
  DatabaseFileLabel,
  BuildingUnit,
  BuildingAttribute,
  ClassificationResult,
} from "../types";

interface UseDropzoneDataReturn {
  dropzoneData: DropzoneData | null;
  setDropzoneData: React.Dispatch<React.SetStateAction<DropzoneData | null>>;
  loading: boolean;
  error: string | null;
  fileLabels: DatabaseFileLabel[];
  setFileLabels: React.Dispatch<React.SetStateAction<DatabaseFileLabel[]>>;
  buildingUnits: BuildingUnit[];
  buildingAttributes: BuildingAttribute[];
  refetchDropzoneFiles: () => Promise<void>;
}

export function useDropzoneData(
  buildingId: string,
  session: any,
  onCachedAnalysesLoaded: (results: Record<string, ClassificationResult>) => void
): UseDropzoneDataReturn {
  const [dropzoneData, setDropzoneData] = useState<DropzoneData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const hasFetchedRef = useRef(false);

  const [fileLabels, setFileLabels] = useState<DatabaseFileLabel[]>([]);
  const [buildingUnits, setBuildingUnits] = useState<BuildingUnit[]>([]);
  const [buildingAttributes, setBuildingAttributes] = useState<BuildingAttribute[]>([]);

  const populateFromCache = useCallback(
    (data: DropzoneData) => {
      if (data.cachedAnalyses && Object.keys(data.cachedAnalyses).length > 0) {
        console.log(
          `📊 [DropzoneTab] Pre-populating ${Object.keys(data.cachedAnalyses).length} cached analyses`
        );
        const results: Record<string, ClassificationResult> = {};
        for (const [fileId, cached] of Object.entries(data.cachedAnalyses)) {
          results[fileId] = {
            suggestedLabels: cached.suggestedLabels,
            documentSummary: cached.documentSummary,
            dueDiligenceAnswers: cached.dueDiligenceAnswers,
            extractedStandardFields: cached.extractedStandardFields,
            fromCache: true,
            analyzedAt: cached.analyzedAt,
            detectedUnitId: cached.detectedUnitId,
            detectedAttributeId: cached.detectedAttributeId,
            suggestsUnit: cached.suggestsUnit,
            suggestsAttribute: cached.suggestsAttribute,
          };
        }
        onCachedAnalysesLoaded(results);
      }
    },
    [onCachedAnalysesLoaded]
  );

  const refetchDropzoneFiles = useCallback(async () => {
    if (!buildingId || !session) return;

    setLoading(true);
    setError(null);

    try {
      const url = `${API_URL}/building/${buildingId}/dropzone-files`;
      const response = await fetchWithAuth(url, { method: "GET" }, session);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.message || `Fout bij ophalen bestanden (${response.status})`
        );
      }

      const data: DropzoneData = await response.json();
      setDropzoneData(data);
      populateFromCache(data);
    } catch (err: any) {
      console.error("Error fetching dropzone files:", err);
      Sentry.captureException(err, {
        extra: { buildingId, context: "DropzoneTab refetch" },
      });
      setError(
        err.message || "Er ging iets mis bij het ophalen van de bestanden"
      );
    } finally {
      setLoading(false);
    }
  }, [buildingId, session, populateFromCache]);

  // Reset when buildingId changes
  useEffect(() => {
    hasFetchedRef.current = false;
    setDropzoneData(null);
    setLoading(true);
    setError(null);
  }, [buildingId]);

  // Fetch file labels on mount
  useEffect(() => {
    const fetchFileLabels = async () => {
      if (!session) return;

      try {
        const response = await fetchWithAuth(
          `${API_URL}/file/search-labels?term=&resourceTypes=building,unit&documentTypes=document`,
          { method: "GET" },
          session
        );

        if (response.ok) {
          const labels: DatabaseFileLabel[] = await response.json();
          labels.sort((a, b) => a.label.localeCompare(b.label));
          setFileLabels(labels);
        }
      } catch (err) {
        console.error("Error fetching file labels:", err);
      }
    };

    fetchFileLabels();
  }, [session]);

  // Fetch building units
  useEffect(() => {
    const fetchBuildingUnits = async () => {
      if (!session || !buildingId) return;

      try {
        const response = await fetchWithAuth(
          `${API_URL}/building/${buildingId}/units`,
          { method: "GET" },
          session
        );

        if (response.ok) {
          const units = await response.json();
          setBuildingUnits(units);
        }
      } catch (err) {
        console.error("Error fetching building units:", err);
      }
    };

    fetchBuildingUnits();
  }, [session, buildingId]);

  // Fetch building attributes
  useEffect(() => {
    const fetchBuildingAttributes = async () => {
      if (!session || !buildingId) return;

      try {
        const response = await fetchWithAuth(
          `${API_URL}/building-attributes/building/${buildingId}`,
          { method: "GET" },
          session
        );
        if (response.ok) {
          const data = await response.json();
          setBuildingAttributes(Array.isArray(data) ? data : []);
        }
      } catch (error) {
        console.error("Error fetching building attributes:", error);
        setBuildingAttributes([]);
      }
    };

    fetchBuildingAttributes();
  }, [session, buildingId]);

  // Initial dropzone files fetch
  useEffect(() => {
    if (hasFetchedRef.current || !buildingId || !session) {
      return;
    }

    const fetchDropzoneFiles = async () => {
      hasFetchedRef.current = true;

      try {
        const url = `${API_URL}/building/${buildingId}/dropzone-files`;
        const response = await fetchWithAuth(url, { method: "GET" }, session);

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(
            errorData.message || `Fout bij ophalen bestanden (${response.status})`
          );
        }

        const data: DropzoneData = await response.json();
        setDropzoneData(data);
        populateFromCache(data);
      } catch (err: any) {
        console.error("Error fetching dropzone files:", err);
        Sentry.captureException(err, {
          extra: { buildingId, context: "DropzoneTab fetch" },
        });
        setError(
          err.message || "Er ging iets mis bij het ophalen van de bestanden"
        );
      } finally {
        setLoading(false);
      }
    };

    fetchDropzoneFiles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [buildingId, !!session]);

  return {
    dropzoneData,
    setDropzoneData,
    loading,
    error,
    fileLabels,
    setFileLabels,
    buildingUnits,
    buildingAttributes,
    refetchDropzoneFiles,
  };
}
